/*
 *
 * PblManagementActionsModal
 *
 * @version 1.0.0
 * @author Davide Polci
 * @since 10-Jul-2025
 */

import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'
import DatePicker from '@nexidigital/datepicker'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import MerchantModal from '@nexidigital/merchantmodal'
import MerchantTimePicker from '@nexidigital/merchanttimepicker'
import PortalSwitch from '@nexidigital/portalswitch'
import PortalTextInput from '@nexidigital/portaltextinput'
import { isEqual } from 'lodash'
import { momentLongDateFormat, timezoneDateConverter, datePickerEternalMaxDate } from 'Utility/dateMethods'
import { is12HourFormat } from 'Utility/globalMethods'
import { getLanguageCalendar } from 'Utility/globalMethods'
import { handleOnlyNumberKeyDown } from 'Utility/numberMethods'
import formattedMessage, { captions, messages } from './languageModule'
import './PblManagementActionsModal.scss'

const PblManagementActionsModal = ({ data, actions, isLoading }) => {
  const formatDate = momentLongDateFormat()
  const intl = useIntl()
  const formattedDate = data?.data?.expDate ? moment(timezoneDateConverter(data?.data?.expDate), 'L HH:mm:ss') : null
  const formik = useFormik({
    initialValues: {
      expirationDateTime: moment(data?.data?.expDate).isValid()
        ? moment(formattedDate).format(formatDate)
        : '',
      longTermSessionId: data?.data?.linkId || '',
      usages: data?.data?.uses || 0,
      expTime: moment(data?.data?.expDate).isValid()
        ? moment(timezoneDateConverter(data?.data?.expDate), 'L HH:mm:ss')
        : '',
      status: data?.data?.maxPayments === '0' ? 'INACTIVE' : data?.data?.status,
      maxPayments: data?.data?.paymentType !== 'UNLIMITED' ? data?.data?.maxPayments || '' : '',
      amountType: data?.data?.amountType || '',
      predefinedAmounts: data?.data?.amount || ''
    },
    validationSchema: Yup.object({
      expTime: Yup.string()
        .nullable()
        .required(
          intl.formatMessage({
            id: 't.app.pblManagement.research.validator.expTimeRequired',
            defaultMessage: 'Expiration time is required'
          })
        )
        .test(
          'is-future-time-if-today',
          intl.formatMessage({
            id: 't.app.pblManagement.research.validator.expTimeFuture',
            defaultMessage: 'Expiration time must be in the future if expiration date is today'
          }),
          function (value) {
            if (!value) return true
            const expDate = moment(this.parent.expirationDateTime, formatDate)
            if (expDate && moment(expDate).isSame(moment(), 'day')) {
              const selectedTime = moment(value)
              return selectedTime.isAfter(moment())
            }
            return true
          }
        ),
      expirationDateTime: Yup.string()
        .required(
          intl.formatMessage({
            id: 't.app.pblManagement.research.validator.expDateRequired',
            defaultMessage: 'Expiration date is required'
          })
        )
        .test(
          'is-not-past',
          intl.formatMessage({
            id: 't.app.pblManagement.research.validator.expDatePast',
            defaultMessage: 'Expiration date and time cannot be in the past'
          }),
          function (value) {
            const formattedDate = moment(value, formatDate)
            const formattedExpTime = this.parent.expTime
            if (!value || !moment(formattedDate).isValid()) return true
            return moment(formattedExpTime).isAfter(moment())
          }
        ),
      maxPayments: Yup.number()
        .nullable()
        .test(
          'is-greater-than-maxNumUses',
          intl.formatMessage({
            id: 't.app.pblManagement.research.validator.maxPaymentsGreaterThanUses',
            defaultMessage: 'Max payments must be greater than uses'
          }),
          function (value) {
            const { usages } = this.parent
            if (
              value === undefined ||
              value === null ||
              usages === undefined ||
              usages === null ||
              (formik.values.maxPayments === '1' && formik.values.amountType === 'FIXED' && formik.values.usages > 0)
            )
              return true
            return Number(value) > Number(usages)
          }
        )
        .test(
          'is-required',
          intl.formatMessage({
            id: 't.app.project.error.isRequired',
            defaultMessage: 'This field is required'
          }),
          function (value) {
            return data?.data?.paymentType === 'MULTIPLE_PAYMENT' ? !!value : true
          }
        )
    })
  })

  const isActionDelete = data?.action === 'delete'
  const isActionManage = data?.action === 'manage'
  const isOnePayment = data?.data?.paymentType === 'ONE_PAYMENT'

  const handleSwitchChange = (selectedOption) => {
    formik.setFieldValue('status', selectedOption ? 'ACTIVE' : 'INACTIVE')
  }

  useEffect(() => { 
    formik.setFieldTouched('expTime', true)
  }, [formik?.values?.expTime])

  useEffect(() => {
    if (!isEqual(formik.values, formik.initialValues) && formik.values.status === 'ACTIVE') {
      // Show errors if there are any
      Object.keys(formik.values).forEach((field) => {
        formik.setFieldTouched(field, true)
      })
    }

    if (formik.values.status === 'ACTIVE' && (formik.errors.expirationDateTime || formik.errors.expTime)) {
      formik.setFieldValue('expirationDateTime', '')
      formik.setFieldValue('expTime', '')
    }
    formik.setFieldValue('maxPayments', formik?.initialValues?.maxPayments)
  }, [formik.values.status])

  const handleChangeNumUses = (name, value) => {
    if (!/^[0-9]*$/.test(value)) return
    formik.setFieldValue(name, value)
  }

  const onSubmit = (values) => {
    actions.submitForm({
      name: 'PblManagementActionForm',
      data:
        (formik.initialValues.status === 'ACTIVE' && values.status === 'INACTIVE') || (isOnePayment && isActionManage)
          ? { ...values, expirationDateTime: null, maxPayments: null }
          : isActionDelete
            ? { ...values, status: 'DELETED', expirationDateTime: null, maxPayments: null }
            : { ...values }
    })
  }

  return (
    <MerchantModal
      closeLabel={intl.formatMessage(captions.close)}
      bigPopup
      title={isActionDelete && formattedMessage.titleModalDelete(formik?.values.longTermSessionId)}
      description={isActionDelete && formattedMessage.subTitleModalDelete}
      topBarTitle={isActionManage && formattedMessage.titleModalManage(formik?.values.longTermSessionId)}
      onClose={() => actions.closePopup()}
      primaryButtonLabel={formattedMessage.confirm}
      primaryAction={() => onSubmit(formik.values)}
      primaryButtonDisabled={isActionManage && (!formik.isValid || isEqual(formik.values, formik.initialValues))}
      primaryButtonLoading={isLoading}>
      {isActionManage && (
        <MerchantGenericCard>
          <div className='pblManagement-modal-form'>
            <div className='form-section'>
              <div className='form-row switch'>
                <h5 className='subtitle'>{formattedMessage.subTitleModalManage}</h5>
                <PortalSwitch
                  id='switchUserStatus'
                  ariaLabel={intl.formatMessage(captions.status)}
                  checked={formik.values.status === 'ACTIVE'}
                  activeLabel={formattedMessage.activated}
                  disabled={
                    data?.data?.status === 'COMPLETED' ||
                    (formik.values.status === 'INACTIVE' &&
                      moment(data?.data?.expDate).isBefore(moment()) &&
                      isOnePayment)
                  }
                  deactiveLabel={formattedMessage.deactivated}
                  onChange={(selectedOption) => handleSwitchChange(selectedOption)}
                  error={formik.touched.status && formik.errors.status}
                  errorText={formik.errors.status}
                />
              </div>

              <div>
                <div className='form-row'>
                  <DatePicker
                    placeholder1={formatDate}
                    id1='pblManagement_modal_expDate'
                    disabled={formik.values.status === 'INACTIVE' || isOnePayment}
                    minDate={moment().startOf('day').format('YYYY-MM-DD')}
                    maxDate={datePickerEternalMaxDate}
                    locale={getLanguageCalendar()}
                    onChange1={(date) => {
                      if (formik.values.status !== 'INACTIVE') {
                        formik.setFieldValue('expirationDateTime', date)
                        formik.setFieldValue('expTime', moment(date, formatDate).endOf('day'))
                      }
                    }}
                    onBlur1={() => {
                      if (formik.values.status !== 'INACTIVE') formik.setFieldTouched('expirationDateTime', true)
                    }}
                    value1={formik?.values?.expirationDateTime || ''}
                    label1={`${intl.formatMessage(captions.expiringDate)}*`}
                    error1={!!formik.errors?.expirationDateTime && !!formik.touched?.expirationDateTime}
                    errorMessage1={formik?.errors?.expirationDateTime}
                    ariaLabelCalendarButton={intl.formatMessage(captions.calendar)}
                  />

                  <MerchantTimePicker
                    label={`${intl.formatMessage(captions.expiringTime)}*`}
                    disabled={formik.values.status === 'INACTIVE' || isOnePayment}
                    format={is12HourFormat() ? 'hh:mm aa' : 'hh:mm'}
                    value={isNaN(moment(formik?.values?.expTime)) ? '' : new Date(moment(formik?.values?.expTime))}
                    onFocus={() => formik.setFieldTouched('expTime', false)}
                    onChange={(value) => {
                      formik.setFieldValue('expTime', value)
                    }}
                    onBlur={() => formik.setFieldTouched('expTime', true)}
                    name='pblManagement_modal_expTime'
                    error={
                      (formik?.errors?.expirationDateTime || formik?.errors?.expTime) && formik?.touched.expTime
                        ? formik?.errors?.expTime
                        : null
                    }
                  />
                </div>
                <div className='form-row'>
                  <div className='pblManagement_modal_maxPayments_input'>
                    {!(
                      formik.initialValues.maxPayments === '1' &&
                      formik.initialValues.amountType === 'FIXED' &&
                      formik.initialValues.usages > 0
                    ) && (
                      <PortalTextInput
                        ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
                        id='pblManagement_modal_maxPayments'
                        type='text'
                        label={intl.formatMessage(captions.maxPayments)}
                        placeholderBlank
                        disabled={formik.values.status === 'INACTIVE' || data?.data?.paymentType !== 'MULTIPLE_PAYMENT'}
                        value={formik.values.maxPayments}
                        onChange={(e) => handleChangeNumUses('maxPayments', e.target.value)}
                        onBlur={() => formik.setFieldTouched('maxPayments', true)}
                        error={formik.errors.maxPayments && formik.touched?.maxPayments}
                        errorText={formik.errors?.maxPayments}
                        onKeyDown={handleOnlyNumberKeyDown}
                      />
                    )}
                  </div>
                  <p className='pblManagement_modal_usesText'>
                    {formattedMessage.uses} {formik.values.usages}
                  </p>
                </div>
                <p className='pblManagement_modal_mandatory' aria-label={intl.formatMessage(captions.mandatoryField)}>
                  {formattedMessage.mandatoryField}
                </p>
              </div>
            </div>
          </div>
        </MerchantGenericCard>
      )}
    </MerchantModal>
  )
}

PblManagementActionsModal.propTypes = {
  actions: PropTypes.object,
  data: PropTypes.shape({
    data: PropTypes.shape({
      expDate: PropTypes.string,
      linkId: PropTypes.string,
      uses: PropTypes.number,
      status: PropTypes.string,
      maxPayments: PropTypes.string,
      amountType: PropTypes.string,
      amount: PropTypes.string,
      paymentType: PropTypes.string
    }),
    action: PropTypes.string
  }).isRequired,
  isLoading: PropTypes.bool
}

PblManagementActionsModal.defaultProps = {
  actions: {},
  isLoading: false
}

export default PblManagementActionsModal
